<template>
  <button
    class="base-button"
    v-bind="$attrs"
  :class="[sizeClass, variantClass, { 'base-button--icon-only': isIconOnly, 'base-button--circle': isCircle }]"
    :type="type"
    :disabled="disabled"
    :aria-label="$attrs['aria-label'] || title || hint || iconAlt"
    :title="$attrs.title || hint || null"
    @click="handleClick"
  >
    <span v-if="$slots.icon || icon" class="base-button__icon">
      <slot name="icon">
        <img v-if="isIconImage" :src="icon" :alt="iconAlt" />
        <i v-else :class="icon" aria-hidden="true"></i>
      </slot>
    </span>
  <span v-if="hasTitle && !isCircle" class="base-button__title">{{ title }}</span>
    <span v-if="$slots.icon || icon" class="base-button__spacer" aria-hidden="true"></span>
  </button>
</template>

<script setup>
/* global defineProps, defineEmits, defineOptions */
import { computed } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  size: {
    type: String,
    default: 'md',
  },
  title: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  iconAlt: {
    type: String,
    default: 'button icon',
  },
  hint: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'button',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'primary',
    validator: (v) => [
      'primary',
      'secondary',
      'success',
      'danger',
      'compact',
      'outline',
      'outline-success',
      'outline-danger',
      'ghost',
      'cancel',
    ].includes(v),
  },
  shape: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'circle'].includes(v),
  },
});

const emit = defineEmits(['click']);

const sizeClass = computed(() => {
  const sizes = {
    sm: 'base-button--sm',
    md: 'base-button--md',
    lg: 'base-button--lg',
  };
  return sizes[props.size] || sizes.md;
});

const variantClass = computed(() => {
  const variants = {
    primary: 'base-button--primary',
    secondary: 'base-button--secondary',
    success: 'base-button--success',
    danger: 'base-button--danger',
    compact: 'base-button--compact',
    outline: 'base-button--outline',
    'outline-success': 'base-button--outline-success',
    'outline-danger': 'base-button--outline-danger',
    ghost: 'base-button--ghost',
    cancel: 'base-button--cancel',
  };
  return variants[props.variant] || variants.primary;
});

const isIconImage = computed(() => {
  if (!props.icon) return false;
  return (
    props.icon.startsWith('/') ||
    props.icon.startsWith('http') ||
    props.icon.startsWith('data:') ||
    props.icon.includes('.')
  );
});

const hasTitle = computed(() => {
  return Boolean(props.title && String(props.title).trim().length);
});

const isCircle = computed(() => props.shape === 'circle');

const isIconOnly = computed(() => {
  return !hasTitle.value || isCircle.value;
});

function handleClick(event) {
  if (props.disabled) return;
  emit('click', event);
}
</script>

<style scoped>
.base-button {
  /* Center title regardless of icon presence */
  grid-auto-flow: column;
  align-items: center;
  column-gap: 8px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 400; /* changed: remove bold */
  color: var(--text-color-button, #ffffff);
  background-color: var(--background-color-button, #1476f3);
  transition: background-color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.base-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.base-button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.base-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.base-button__title {
  justify-self: center;
  text-align: center;
}

.base-button__spacer {
  /* Mirrors icon width so the title stays centered */
  width: 18px;
  height: 18px;
}

.base-button__icon img {
  width: 18px;
  height: 18px;
}

.base-button__icon i {
  font-size: 18px;
}

/* Icon-only button (no title) */
.base-button--icon-only {
  grid-template-columns: auto;
  justify-content: center;
  padding: 8px;
  min-width: 40px;
}
.base-button--icon-only .base-button__icon img {
  width: 20px;
  height: 20px;
}
.base-button--icon-only .base-button__icon i {
  font-size: 20px;
}

/* Circle shape */
.base-button--circle {
  border-radius: 50%;
  padding: 0;
  gap: 0;
  justify-content: center;
  align-items: center;
  min-width: 0;
  box-sizing: border-box;
}
.base-button--circle .base-button__title {
  display: none;
}
.base-button--circle.base-button--sm {
  width: 32px;
  height: 32px;
  padding: 0;
}
.base-button--circle.base-button--md {
  width: 40px;
  height: 40px;
  padding: 0;
}
.base-button--circle.base-button--lg {
  width: 48px;
  height: 48px;
  padding: 0;
}
.base-button--circle .base-button__icon img {
  width: 20px;
  height: 20px;
}
.base-button--circle .base-button__icon i {
  font-size: 20px;
}


.base-button--sm {
  padding: 6px 12px;
  font-size: 12px;
}

.base-button--md {
  padding: 10px 16px;
  font-size: 14px;
}

.base-button--lg {
  padding: 12px 20px;
  font-size: 16px;
}

/* Compact variant: reduce inner spacing (useful when label should be close to button border)
   Notes:
   - This is intentionally just a spacing modifier.
   - For colors, it follows primary by default.
*/
.base-button--compact {
  background-color: var(--background-color-button-danger, #dc2626);
  color: var(--background-color);
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 8px;
  padding-right: 8px;
}
.base-button--compact:hover:not(:disabled) {
 background-color: var(--background-color-button-danger-hover, #b91c1c);
}

/* Variant styles */
.base-button--primary {
  background-color: var(--background-color-button, #1476f3);
  color: var(--text-color-button, #ffffff);
}
.base-button--primary:hover:not(:disabled) {
  background-color: var(--background-color-button-hover, #1c407a);
}

.base-button--secondary {
  background-color: var(--background-color-button-secondary, #6b7280);
  color: var(--text-color-button-secondary, #ffffff);
}
.base-button--secondary:hover:not(:disabled) {
  background-color: var(--background-color-button-secondary-hover, #4b5563);
}

.base-button--success {
  background-color: var(--background-color-button-success, #16a34a);
  color: #ffffff;
}
.base-button--success:hover:not(:disabled) {
  background-color: var(--background-color-button-success-hover, #0f8a3a);
}

.base-button--danger {
  background-color: var(--background-color-button-danger, #dc2626);
  color: var(--background-color)
}
.base-button--danger:hover:not(:disabled) {
  background-color: var(--background-color-button-danger-hover, #b91c1c);
}

.base-button--outline {
  background: transparent;
  color: var(--background-color-button, #1476f3);
  border: 1px solid var(--background-color-button, #1476f3);
}
.base-button--outline:hover:not(:disabled) {
  /* follow theme primary color (uses --background-color-button-rgb) */
  background-color: rgba(var(--background-color-button-rgb, 20, 116, 242), 0.06);
}

.base-button--outline-success {
  background: transparent;
  color: var(--background-color-button-success, #16a34a);
  border: 1px solid var(--background-color-button-success, #16a34a);
}
.base-button--outline-success:hover:not(:disabled) {
  /* follow theme success color when available */
  background-color: rgba(var(--background-color-button-success-rgb, 22, 163, 74), 0.06);
}

.base-button--outline-danger {
  background: transparent;
  color: var(--background-color-button-danger, #dc2626);
  border: 1px solid var(--background-color-button-danger, #dc2626);
}
.base-button--outline-danger:hover:not(:disabled) {
  /* follow theme danger color when available */
  background-color: rgba(var(--background-color-button-danger-rgb, 220, 38, 38), 0.06);
}

/* Cancel variant (used for "انصراف") */
.base-button--cancel {
  background-color: var(--background-color-button2);
  color: var(--text-color-primary);
  border: 1px solid var(--text-color-secondary);
}
.base-button--cancel:hover:not(:disabled) {
  /* on hover: background uses --tex-color-forth, text becomes white, slightly translucent */
  background-color: var(--background-color-button-hover2);
  color: var(--background-color);
  border: 1px solid var(--text-color-secondary);
}

.base-button--ghost {
  background: transparent;
  color: var(--tex-color-forth, #383838);
  border: none;
}
.base-button--ghost:hover:not(:disabled) {
  /* ghost hover should also follow primary theme color */
  background-color: rgba(var(--tex-color-forth,  #383838), 0.6);
}
</style>
